/**
 * Sidebar panel for paying an invoice.
 *
 * Fetches payment options (supported types + saved methods) on open,
 * lets the user pick a payment method and amount (partial payments
 * allowed), then submits for immediate capture via the portal API.
 * Consistent UX with the cart checkout payment flow.
 */

import { faCreditCard, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PaymentMethodTypeEnum } from '@supply-mover-2-0/core';
import type {
	PortalCheckoutPaymentMethodDto,
	SupportedPaymentMethodType,
} from '@supply-mover-2-0/shared';
import { Sidebar, useTranslation } from '@supply-mover-2-0/web-ui';
import React, { useCallback, useEffect, useState } from 'react';
import { usePortalAuth } from '../hooks/usePortalAuth';
import { formatCurrency } from '../lib/formatCurrency';
import { triggerLightImpact } from '../lib/haptics';
import * as portalInvoiceService from '../services/portalInvoiceService';
import { PortalAddPaymentMethod } from './PortalAddPaymentMethod';

interface PayInvoiceSidebarProps {
	isOpen: boolean;
	onClose: () => void;
	invoiceId: number;
	outstandingBalance: number;
	invoiceExternalId: string | null;
	onPaymentSuccess: (updatedBalance: string) => void;
}

export const PayInvoiceSidebar: React.FC<PayInvoiceSidebarProps> = ({
	isOpen,
	onClose,
	invoiceId,
	outstandingBalance,
	invoiceExternalId,
	onPaymentSuccess,
}) => {
	const { t } = useTranslation();
	const { token } = usePortalAuth();

	// Payment options from server
	const [paymentTypes, setPaymentTypes] = useState<
		Array<SupportedPaymentMethodType>
	>([]);
	const [savedMethods, setSavedMethods] = useState<
		Array<PortalCheckoutPaymentMethodDto>
	>([]);
	const [optionsLoading, setOptionsLoading] = useState(false);

	// Form state
	const [amount, setAmount] = useState('');
	const [selectedTypeId, setSelectedTypeId] = useState<number | null>(null);
	const [selectedMethodId, setSelectedMethodId] = useState<number | null>(null);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);

	// Add payment method sidebar (nested)
	const [showAddPayment, setShowAddPayment] = useState(false);

	// Fetch payment options when sidebar opens
	const fetchPaymentOptions = useCallback(async () => {
		if (token == null) {
			return;
		}

		setOptionsLoading(true);
		try {
			const res = await portalInvoiceService.getInvoicePaymentOptions(
				token,
				invoiceId,
			);
			if (res.success) {
				setPaymentTypes(res.data.paymentTypes);
				setSavedMethods(res.data.savedPaymentMethods);

				// Auto-select first payment type
				if (res.data.paymentTypes.length > 0) {
					setSelectedTypeId(res.data.paymentTypes[0].id);
				}

				// Auto-select primary saved method
				const primary = res.data.savedPaymentMethods.find((m) => m.isPrimary);
				if (primary != null) {
					setSelectedMethodId(primary.id);
				} else if (res.data.savedPaymentMethods.length > 0) {
					setSelectedMethodId(res.data.savedPaymentMethods[0].id);
				}
			}
		} catch (err) {
			console.error('Failed to fetch payment options:', err);
			setError(t('Failed to load payment options'));
		} finally {
			setOptionsLoading(false);
		}
	}, [token, invoiceId, t]);

	useEffect(() => {
		if (isOpen) {
			// Reset state when opening
			setAmount(outstandingBalance.toFixed(2));
			setError(null);
			setSubmitting(false);
			void fetchPaymentOptions();
		}
	}, [isOpen, outstandingBalance, fetchPaymentOptions]);

	// Currently selected payment type object
	const selectedType = paymentTypes.find((pt) => pt.id === selectedTypeId);

	// Filter saved methods to match the selected payment type
	const methodsForType = savedMethods.filter(
		(m) => m.type === selectedType?.paymentMethodType,
	);

	// Processing fee preview
	const parsedAmount = parseFloat(amount);
	const isValidAmount =
		!isNaN(parsedAmount) &&
		parsedAmount > 0 &&
		parsedAmount <= outstandingBalance;
	const feeAmount =
		selectedType != null && isValidAmount
			? selectedType.processingFeeType === '%'
				? (parsedAmount * selectedType.processingFee) / 100
				: selectedType.processingFee
			: 0;
	const totalWithFee = isValidAmount ? parsedAmount + feeAmount : 0;

	const canSubmit =
		isValidAmount &&
		selectedTypeId != null &&
		selectedMethodId != null &&
		!submitting;

	const handleSubmit = async (): Promise<void> => {
		if (token == null || !canSubmit) {
			return;
		}

		setSubmitting(true);
		setError(null);

		try {
			const res = await portalInvoiceService.payInvoice(token, invoiceId, {
				paymentAmount: parsedAmount,
				paymentMethodId: selectedMethodId,
			});

			if (res.success) {
				// PD-14695: a light tap confirms the payment landed.
				triggerLightImpact();
				onPaymentSuccess(res.updatedOutstandingBalance);
				onClose();
			}
		} catch (err) {
			const message =
				err instanceof Error
					? err.message
					: t('Payment failed. Please try again.');
			setError(message);
		} finally {
			setSubmitting(false);
		}
	};

	// After adding a new payment method, refresh options and auto-select it
	const handleAddPaymentSuccess = (): void => {
		setShowAddPayment(false);
		void fetchPaymentOptions();
	};

	return (
		<>
			<Sidebar
				isOpen={isOpen}
				onClose={onClose}
				title={t('Pay Invoice')}
				width="md"
				position="right"
				modal={true}
			>
				<div className="p-4">
					{/* Invoice reference */}
					<div className="mb-4 pb-4 border-b border-brand-border">
						<p className="text-sm text-brand-muted">
							{t('Invoice')} #{t(invoiceExternalId ?? String(invoiceId))}
						</p>
						<p className="text-lg font-bold text-gray-900 dark:text-gray-100">
							{t('Outstanding')}: {t(formatCurrency(outstandingBalance) ?? '')}
						</p>
					</div>

					{optionsLoading && (
						<div className="flex items-center justify-center py-8">
							<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-portal-accent" />
						</div>
					)}

					{!optionsLoading && (
						<>
							{/* Amount */}
							<div className="mb-4">
								<label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
									{t('Payment Amount')}
								</label>
								<div className="relative">
									<span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
										$
									</span>
									<input
										type="number"
										value={amount}
										onChange={(e) => {
											setAmount(e.target.value);
											setError(null);
										}}
										min="0.01"
										max={outstandingBalance}
										step="0.01"
										className="w-full pl-7 pr-3 py-2.5 rounded-lg bg-brand-input border border-brand-border text-sm text-gray-900 dark:text-gray-100"
									/>
								</div>
								{!isNaN(parsedAmount) && parsedAmount > outstandingBalance && (
									<p className="text-xs text-red-500 mt-1">
										{t('Cannot exceed outstanding balance')}
									</p>
								)}
							</div>

							{/* Payment type */}
							{paymentTypes.length > 0 && (
								<div className="mb-4">
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
										{t('Payment Type')}
									</label>
									<div className="space-y-2">
										{paymentTypes.map((pt) => (
											<label
												key={pt.id}
												className="flex items-center gap-3 cursor-pointer p-2 -mx-2 rounded-lg hover:bg-brand-input"
											>
												<input
													type="radio"
													name="invoicePaymentType"
													checked={selectedTypeId === pt.id}
													onChange={() => {
														setSelectedTypeId(pt.id);
														// Auto-select first method for this type
														const methods = savedMethods.filter(
															(m) => m.type === pt.paymentMethodType,
														);
														const primary = methods.find((m) => m.isPrimary);
														setSelectedMethodId(
															primary?.id ?? methods[0]?.id ?? null,
														);
													}}
													className="w-4 h-4 text-portal-accent"
												/>
												<FontAwesomeIcon
													icon={faCreditCard}
													className="text-gray-400"
												/>
												<span className="text-sm text-gray-900 dark:text-gray-100">
													{t(pt.paymentMethodType)}
												</span>
												{pt.processingFee > 0 && (
													<span className="text-xs text-gray-400 ml-auto">
														{pt.processingFeeType === '%'
															? `${pt.processingFee}% ${t('fee')}`
															: `${formatCurrency(pt.processingFee)} ${t('fee')}`}
													</span>
												)}
											</label>
										))}
									</div>
								</div>
							)}

							{/* Saved payment methods */}
							{selectedTypeId != null && (
								<div className="mb-4">
									<label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
										{t('Payment Method')}
									</label>
									{methodsForType.length > 0 ? (
										<select
											value={selectedMethodId ?? ''}
											onChange={(e) =>
												setSelectedMethodId(parseInt(e.target.value, 10))
											}
											className="w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-gray-900 dark:text-gray-100"
										>
											{methodsForType.map((m) => (
												<option key={m.id} value={m.id}>
													{t(m.subtype ?? m.type)}{' '}
													{m.lastFour != null ? `****${m.lastFour}` : ''}{' '}
													{m.name != null ? `(${t(m.name)})` : ''}
												</option>
											))}
										</select>
									) : (
										<p className="text-xs text-brand-muted py-1">
											{t('No saved payment methods for this type.')}
										</p>
									)}
									<button
										type="button"
										onClick={() => setShowAddPayment(true)}
										className="flex items-center gap-1.5 text-xs font-medium text-portal-accent hover:underline mt-2"
									>
										<FontAwesomeIcon icon={faPlus} />
										<span>{t('Add new payment method')}</span>
									</button>
								</div>
							)}

							{/* Fee preview */}
							{feeAmount > 0 && isValidAmount && (
								<div className="mb-4 p-3 bg-brand-input rounded-lg">
									<div className="flex justify-between text-sm">
										<span className="text-brand-muted">{t('Payment')}</span>
										<span className="text-gray-900 dark:text-gray-100">
											{t(formatCurrency(parsedAmount) ?? '')}
										</span>
									</div>
									<div className="flex justify-between text-sm mt-1">
										<span className="text-brand-muted">
											{t('Processing fee')}
										</span>
										<span className="text-gray-900 dark:text-gray-100">
											{t(formatCurrency(feeAmount) ?? '')}
										</span>
									</div>
									<div className="flex justify-between text-sm font-semibold mt-1 pt-1 border-t border-brand-border">
										<span className="text-gray-900 dark:text-gray-100">
											{t('Total charge')}
										</span>
										<span className="text-gray-900 dark:text-gray-100">
											{t(formatCurrency(totalWithFee) ?? '')}
										</span>
									</div>
								</div>
							)}

							{/* Error */}
							{error != null && (
								<div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
									<p className="text-sm text-red-600 dark:text-red-400">
										{t(error)}
									</p>
								</div>
							)}

							{/* Submit */}
							<button
								type="button"
								onClick={() => void handleSubmit()}
								disabled={!canSubmit}
								className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-portal-accent hover:bg-portal-accent-hover disabled:bg-gray-300 dark:disabled:bg-gray-700 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors"
							>
								{submitting ? (
									<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
								) : null}
								{submitting
									? t('Processing...')
									: t(
											`Pay ${formatCurrency(isValidAmount ? parsedAmount : 0)}`,
										)}
							</button>
						</>
					)}
				</div>
			</Sidebar>

			{/* Nested add payment method sidebar */}
			<Sidebar
				isOpen={showAddPayment}
				onClose={() => setShowAddPayment(false)}
				title={t('Add Payment Method')}
				width="md"
				position="right"
				modal={true}
			>
				<div className="p-4">
					<PortalAddPaymentMethod
						onSuccess={handleAddPaymentSuccess}
						onCancel={() => setShowAddPayment(false)}
						supportedPaymentMethods={paymentTypes}
						preSelectedPaymentType={
							selectedType?.paymentMethodType === 'ACH'
								? PaymentMethodTypeEnum.ACH
								: selectedType?.paymentMethodType === 'Credit Card'
									? PaymentMethodTypeEnum.CREDIT_CARD
									: undefined
						}
					/>
				</div>
			</Sidebar>
		</>
	);
};
